<script setup lang="ts">
// Confirmação ao DESATIVAR as notificações de marketing (ENG-491).
// Figma "User ativo" (upMg3vmJ82lB1DspMBbRhe, node 23655:89554): é a FOLHA da
// roletinha/check-in (mesma geometria do BaseNoticeSheet — arte de 277,3×218,3
// sangrando 75px acima do painel de 393px), só que com luz vermelha, o
// triângulo com sino na janela, dois sinos soltos nas laterais e a checklist
// do que o usuário perde. Ligar não passa por aqui (useMarketingOptIn).
//
// Medidas verticais em % da LARGURA (padding/margin resolvem pela largura),
// como no NoticeSheet: 393px do Figma = 100%. Nunca % NA folha (o bloco
// contenedor é o overlay — viraria fração da viewport).
import { getAssetsUrlForCDN } from '@/utils/template';

const isOpen = defineModel<boolean>({ required: true });

// pelo CDN estático (appStaticCdn), como os outros assets do painel
const ART_MAIN = getAssetsUrlForCDN(
  '/assets/images/user-v2/optout-bell-main.webp',
);

const ART_SIDE = getAssetsUrlForCDN(
  '/assets/images/user-v2/optout-bell-side.webp',
);

const emit = defineEmits<{ confirm: [] }>();

useBodyScrollLock(isOpen);

// Lista e título vieram do Figma/mock do Mario/Raphael (2026-08-27). Nota: o
// toggle controla só consentimento de marketing (mkt_accepted_at) — o front não
// bloqueia roleta/XP por ele; "perderá acesso" é decisão de produto. O Figma
// repete "Acesso antecipado" duas vezes; aqui fica uma.
const LOSSES = [
  'Roleta diária',
  'Acesso antecipado',
  'Benefícios somente para notificações ativas',
  'Novidades e eventos surpresas',
  'Melhorias no clube de fidelidade',
  'Sistema de XP.',
];

function close() {
  isOpen.value = false;
}

function confirmOptOut() {
  close();
  emit('confirm');
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="user-v2-marketing-optout-modal fixed inset-0 z-[100] flex items-end justify-center font-sans lg:items-center lg:p-[16px]"
      @dragstart.prevent
    >
      <div
        class="modal-backdrop absolute inset-0 bg-black/70"
        @click="close"
      />

      <div class="mko modal-card">
        <!-- Cabeçalho FORA do painel: janela mascarada da arte (sangra 75px acima)
             + X. A caixa é a régua do X (top em % da altura da arte). -->
        <div class="mko__head">
          <div class="mko__window">
            <!-- Encaixe medido no SCREENSHOT do Figma, não na geometria do node
                 (o crop/escala do fill não vem no metadata): triângulo com
                 ~65% da folha de largura, centrado, base em y≈195 da folha. O
                 canvas do webp tem o triângulo em 82,6% da largura e base em
                 86% da altura → canvas 111% da janela, top -33,5%. -->
            <img
              alt=""
              aria-hidden="true"
              class="mko__art"
              draggable="false"
              :src="ART_MAIN"
            />
          </div>

          <button
            aria-label="Fechar"
            class="mko__close"
            type="button"
            @click="close"
          >
            <svg
              aria-hidden="true"
              fill="none"
              viewBox="0 0 30 30"
            >
              <rect
                fill="#fff"
                fill-opacity="0.05"
                height="30"
                rx="15"
                width="30"
              />
              <path
                d="M19.2784 12.1583L20 11.4399L18.5601 10L17.8417 10.7184L15 13.5601L12.1583 10.7184L11.4399 10L10 11.4399L10.7184 12.1583L13.5601 15L10.7216 17.8417L10 18.5601L11.4399 20L12.1583 19.2816L15 16.4399L17.8417 19.2816L18.5601 20L20 18.5601L19.2816 17.8417L16.4399 15L19.2816 12.1583H19.2784Z"
                fill="#fff"
              />
            </svg>
          </button>
        </div>

        <div class="mko__panel">
          <!-- "LIGHT": círculo de 513px centrado 140px acima do topo do painel -->
          <div
            aria-hidden="true"
            class="mko__light"
          />

          <p class="mko__title">
            Tem certeza que quer desativar as notificações? Você perderá acesso
            a:
          </p>

          <ul class="mko__list">
            <li
              v-for="loss in LOSSES"
              :key="loss"
              class="mko__item"
            >
              <svg
                aria-hidden="true"
                class="mko__x"
                fill="none"
                viewBox="0 0 16 16"
              >
                <circle
                  cx="8"
                  cy="8"
                  fill="#f31260"
                  fill-opacity="0.35"
                  r="8"
                />
                <path
                  d="M5.4 5.4l5.2 5.2M10.6 5.4l-5.2 5.2"
                  stroke="#ff5c93"
                  stroke-linecap="round"
                  stroke-width="1.6"
                />
              </svg>
              {{ loss }}
            </li>
          </ul>

          <div class="mko__actions">
            <!-- rótulos pedidos pelo Mario (Slack 27/08): perder em vermelho -->
            <button
              class="mko__btn mko__btn--lose"
              type="button"
              @click="confirmOptOut"
            >
              Perder benefícios
            </button>
            <button
              class="mko__btn mko__btn--keep"
              type="button"
              @click="close"
            >
              Manter benefícios
            </button>
          </div>
        </div>

        <!-- Sinos soltos ("image 11" à esquerda, "image 10" à direita, este
             espelhado). Filhos da folha e não do painel: sangram pra fora dele
             e passam por cima da borda. -->
        <img
          alt=""
          aria-hidden="true"
          class="mko__bell mko__bell--left"
          draggable="false"
          :src="ART_SIDE"
        />
        <img
          alt=""
          aria-hidden="true"
          class="mko__bell mko__bell--right"
          draggable="false"
          :src="ART_SIDE"
        />
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.mko {
  @apply relative z-10 select-none;

  width: min(393px, 100vw);
  margin: 0 auto;

  img {
    -webkit-user-drag: none;
  }
}

// Janela do Figma: 277,3 × 218,3, deslocada 5,5px pra direita (1,407%).
.mko__head {
  @apply relative z-[2];
}

.mko__window {
  position: relative;
  left: 1.407%;
  width: 70.56%;
  margin: 0 auto;
  aspect-ratio: 277.291 / 218.318;
  overflow: clip;
  pointer-events: none;
  // dissolve na base pra fundir no painel (mesmo fade do NoticeSheet)
  -webkit-mask-image: linear-gradient(
    to bottom,
    #000 0,
    #000 62.95%,
    transparent 100%
  );
  mask-image: linear-gradient(to bottom, #000 0, #000 62.95%, transparent 100%);
}

.mko__art {
  position: absolute;
  // ponta do triângulo DENTRO da janela (a 111%/-33,5% ela saía cortada): o
  // canvas tem a ponta a 9,7% e a base a 86% da altura → a 96% da janela e
  // top -8% a ponta fica ~9px pra dentro e a base termina no fade.
  top: -8%;
  left: 50%;
  width: 96%;
  max-width: none;
  // 15° horário pra casar com a inclinação do Figma (o raw vem "em pé")
  transform: translateX(-50%) rotate(15deg);
}

// 11px abaixo do topo do painel, medido dentro do cabeçalho (75 + 11) / 218,3
.mko__close {
  @apply absolute z-[3];

  top: 39.47%;
  right: 14px;
  width: 30px;
  height: 30px;

  svg {
    @apply w-full h-full;
  }
}

// margin-top = os 143px que a arte entra no painel; padding-top = 129px do
// Figma até o título.
.mko__panel {
  position: relative;
  z-index: 1;
  margin-top: -36.47%;
  padding: 32.82% 7px calc(env(safe-area-inset-bottom, 0px) + 27px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 0;
  border-radius: 16px 16px 0 0;
  background: #000717;
  // `clip` e não `hidden`: hidden é scroll container (bug das bordas da roletinha)
  overflow: clip;
}

// Luz vermelha (comparado com o render do Figma, não com a geometria do node):
// um DEGRADÊ de borda a borda que toma o topo do painel — cantos incluídos —
// e dissolve até ~30% da altura (antes do título), com um reforço radial atrás
// do triângulo.
// Topo ≈ #e8146c, meio ≈ #920349 (cores medidas no export).
.mko__light {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(
      ellipse 60% 30% at 50% 4%,
      rgb(var(--v2-danger) / 0.45) 0%,
      rgb(var(--v2-danger) / 0) 100%
    ),
    linear-gradient(
      180deg,
      rgb(224 19 107 / 1) 0%,
      rgb(200 12 96 / 0.95) 8%,
      rgb(120 5 65 / 0.6) 18%,
      rgb(0 7 23 / 0) 28%
    );
}

.mko__title,
.mko__list,
.mko__actions {
  @apply relative z-[1];
}

.mko__title,
.mko__item {
  text-shadow:
    0 1px 2px rgba(0, 7, 23, 0.9),
    0 0 10px rgba(0, 7, 23, 0.75);
}

.mko__title {
  @apply text-white font-bold text-center;

  font-size: 18px;
  line-height: 22.5px;
}

// Checklist do Figma: 26px de recuo, itens de 17px com 8px de vão.
.mko__list {
  @apply flex flex-col;

  gap: 8px;
  margin: 28px 0 0 26px;
}

.mko__item {
  @apply flex items-center text-white/85 font-normal;

  gap: 10px;
  font-size: 12px;
  line-height: 17px;
}

.mko__x {
  @apply shrink-0;

  width: 16px;
  height: 16px;
}

.mko__actions {
  @apply flex justify-center;

  gap: 10px;
  margin: 24px 4px 0;
}

.mko__btn {
  @apply flex items-center justify-center font-bold text-white;

  flex: 1 1 0;
  height: 40px;
  padding: 0 8px;
  border-radius: 12px;
  // 13px + sem quebra: os dois rótulos cabem numa linha em 393px
  // (painel 7px + margem 4px + 2 botões com gap 10px)
  font-size: 13px;
  white-space: nowrap;
  text-align: center;
  // halo escuro atrás do rótulo: contraste do branco sobre o verde/cinza
  text-shadow:
    0 1px 2px rgba(0, 7, 23, 0.6),
    0 0 6px rgba(0, 7, 23, 0.35);
}

.mko__btn--lose {
  background: rgb(var(--v2-danger));
}

.mko__btn--keep {
  background: rgb(var(--v2-success));
}

// Sinos laterais, posições em % da folha (Figma: image 11 em x 0,5% / y 114,7px
// e image 10 na borda direita, ~93px).
.mko__bell {
  position: absolute;
  z-index: 3;
  pointer-events: none;
  height: auto;
}

.mko__bell--left {
  top: 18.5%;
  left: 3%;
  width: 14%;
  transform: rotate(-18deg);
}

.mko__bell--right {
  top: 25.5%;
  right: 4%;
  width: 12%;
  // espelhado, o sinal do rotate inverte: -77° aqui = 77° horário na tela
  transform: scaleX(-1) rotate(-77deg);
}

// Desktop: card centralizado com todos os cantos (mesma folha, sem "colar" no
// rodapé).
@media (min-width: 1024px) {
  .mko__panel {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding-bottom: 27px;
  }
}

// main.scss reseta `transition` no app inteiro — entrada via @keyframes
// (mesma estratégia do LogoutConfirmModal.vue).
.modal-backdrop {
  animation: marketing-optout-fade 0.18s ease-out;
}

.modal-card {
  animation: marketing-optout-slide-up 0.32s cubic-bezier(0.22, 0.61, 0.36, 1);

  @media (min-width: 1024px) {
    animation-name: marketing-optout-pop;
    animation-duration: 0.2s;
  }
}

@keyframes marketing-optout-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes marketing-optout-pop {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes marketing-optout-slide-up {
  from {
    opacity: 0;
    transform: translateY(100%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
